import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import {
  kpiDataService,
  KpiMetric,
  KpiCategory,
  useKpiSourcesQuery,
} from 'src/api/startModelingService';
import { useCampaignMappingsQuery } from 'src/api/campaignMappings';
import { useCampaignGroupsQuery } from 'src/api/campaignGroups';
import {
  useCreateModelMutation,
  useUpdateModelMutation,
  useModelDetailsQuery,
  CreateModelInput,
} from 'src/api/models';
import { useCustomToast } from 'src/hooks/useCustomToast';
import {
  TOTAL_CATEGORY_OPTION,
  TOTAL_CATEGORY_VALUE,
} from 'src/utils/kpiConstants';

export type ModelFormData = {
  id: string;
  modelName: string;
  kpiSource: string;
  kpiMetric: string;
  kpiCategory: string;
  selectedMedia: string[];
  campaignSelections: Record<string, boolean>;
};

export type CampaignWithGroup = {
  campaignId: string;
  campaignName: string;
  groupId: string | null;
  groupName: string | null;
};

const createEmptyModel = (id: string): ModelFormData => ({
  id,
  modelName: '',
  kpiSource: '',
  kpiMetric: '',
  kpiCategory: '',
  selectedMedia: [],
  campaignSelections: {},
});

// Map database table names to source identifiers used by the KPI discovery API
const TABLE_TO_SOURCE_MAP: Record<string, string> = {
  u_kpi_data_template: 'uploaded_file',
  d_shopify: 'shopify',
  d_ga4: 'ga4',
};

const mapTableToSource = (tableName: string | null): string => {
  if (!tableName) return '';
  return TABLE_TO_SOURCE_MAP[tableName] || tableName;
};

type UseModelFormOptions = {
  mode: 'create' | 'edit';
  modelId?: string;
  onComplete?: () => void;
};

export const useModelForm = ({
  mode,
  modelId,
  onComplete,
}: UseModelFormOptions) => {
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName ?? '';
  const { toastState, hideToast, showSuccess, showError } = useCustomToast();

  // Models state (array for create, single for edit)
  const [models, setModels] = useState<ModelFormData[]>([
    createEmptyModel('1'),
  ]);
  const [kpiMetrics, setKpiMetrics] = useState<KpiMetric[]>([]);
  const [kpiCategories, setKpiCategories] = useState<KpiCategory[]>([]);
  const [isLoadingKpi, setIsLoadingKpi] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Queries
  const { data: kpiSources = [], isLoading: isLoadingKpiSources } =
    useKpiSourcesQuery(customerId);
  const { data: campaignMappings = [], isLoading: isLoadingCampaigns } =
    useCampaignMappingsQuery(customerId);
  const { data: campaignGroups = [], isLoading: isLoadingGroups } =
    useCampaignGroupsQuery(customerId);
  const { data: modelDetails, isLoading: isLoadingModel } =
    useModelDetailsQuery(
      customerId,
      mode === 'edit' ? (modelId ?? null) : null,
    );

  // Mutations
  const createMutation = useCreateModelMutation(customerId);
  const updateMutation = useUpdateModelMutation(customerId);

  // Derive media platforms from campaign mappings
  const mediaPlatforms = useMemo(() => {
    const sourceMap = new Map<string, string>();

    for (const campaign of campaignMappings) {
      for (const sourceItem of campaign.sources) {
        const source = sourceItem.source?.trim();
        if (source) {
          const key = source.toLowerCase();
          const existing = sourceMap.get(key);
          if (
            !existing ||
            (source[0] === source[0].toUpperCase() &&
              existing[0] === existing[0].toLowerCase())
          ) {
            sourceMap.set(key, source);
          }
        }
      }
    }

    return Array.from(sourceMap.values()).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    );
  }, [campaignMappings]);

  // Derive campaigns with group info (for display in CampaignSelectList)
  const campaignsWithGroups: CampaignWithGroup[] = useMemo(() => {
    // Build a map of campaignId -> group info
    const campaignToGroup = new Map<
      string,
      { groupId: string; groupName: string }
    >();
    campaignGroups.forEach((group) => {
      group.campaignList.forEach((campaign) => {
        campaignToGroup.set(campaign.id, {
          groupId: group.id,
          groupName: group.groupName,
        });
      });
    });

    return campaignMappings.map((cm) => {
      const campaignId = cm.campaign_id.toString();
      const groupInfo = campaignToGroup.get(campaignId);
      return {
        campaignId,
        campaignName: cm.campaign_name,
        groupId: groupInfo?.groupId ?? null,
        groupName: groupInfo?.groupName ?? null,
      };
    });
  }, [campaignMappings, campaignGroups]);

  // KPI category options with "Total" prepended
  const kpiCategoryOptions = useMemo(() => {
    return [TOTAL_CATEGORY_OPTION, ...kpiCategories];
  }, [kpiCategories]);

  // Load KPI metrics and categories for a source
  const loadKpiData = useCallback(
    async (source: string) => {
      if (!customerId || !source) return;

      setIsLoadingKpi(true);
      try {
        const [metrics, categories] = await Promise.all([
          kpiDataService.getMetrics(customerId, source),
          kpiDataService.getCategories(customerId, source),
        ]);
        setKpiMetrics(metrics);
        setKpiCategories(categories);
      } catch {
        setKpiMetrics([]);
        setKpiCategories([]);
        showError('Failed to load KPI data', 'Please try again');
      } finally {
        setIsLoadingKpi(false);
      }
    },
    [customerId, showError],
  );

  // Initialize form for edit mode
  useEffect(() => {
    if (mode === 'edit' && modelDetails && !isInitialized) {
      const campaignSelections: Record<string, boolean> = {};
      modelDetails.campaigns.forEach((c) => {
        campaignSelections[c.campaignId] = !c.blacklist;
      });

      // Map the database table name to the source identifier
      const mappedKpiSource = mapTableToSource(modelDetails.kpiSource);

      setModels([
        {
          id: '1',
          modelName: modelDetails.modelName,
          kpiSource: mappedKpiSource,
          kpiMetric: modelDetails.kpiMetric || '',
          kpiCategory: '',
          selectedMedia: modelDetails.mediaPlatforms,
          campaignSelections,
        },
      ]);

      // Load KPI metrics and categories if source is set
      if (mappedKpiSource) {
        loadKpiData(mappedKpiSource);
      }

      setIsInitialized(true);
    }
  }, [mode, modelDetails, isInitialized, loadKpiData]);

  // Model handlers
  const updateModel = useCallback(
    (id: string, updates: Partial<ModelFormData>) => {
      setModels((prev) =>
        prev.map((m) => (m.id === id ? { ...m, ...updates } : m)),
      );
    },
    [],
  );

  const handleModelNameChange = useCallback(
    (id: string, value: string) => {
      updateModel(id, { modelName: value });
    },
    [updateModel],
  );

  const handleSourceChange = useCallback(
    async (id: string, value: string) => {
      updateModel(id, {
        kpiSource: value,
        kpiMetric: '',
        kpiCategory: '',
      });
      await loadKpiData(value);
    },
    [updateModel, loadKpiData],
  );

  const handleSourceReset = useCallback(
    (id: string) => {
      updateModel(id, {
        kpiSource: '',
        kpiMetric: '',
        kpiCategory: '',
      });
      setKpiMetrics([]);
      setKpiCategories([]);
    },
    [updateModel],
  );

  const handleMetricChange = useCallback(
    (id: string, value: string) => {
      updateModel(id, { kpiMetric: value });
    },
    [updateModel],
  );

  const handleMetricReset = useCallback(
    (id: string) => {
      updateModel(id, { kpiMetric: '' });
    },
    [updateModel],
  );

  const handleCategoryChange = useCallback(
    (id: string, value: string) => {
      updateModel(id, { kpiCategory: value });
    },
    [updateModel],
  );

  const handleCategoryReset = useCallback(
    (id: string) => {
      updateModel(id, { kpiCategory: '' });
    },
    [updateModel],
  );

  const handleMediaChange = useCallback(
    (id: string, selectedMedia: string[]) => {
      updateModel(id, { selectedMedia });
    },
    [updateModel],
  );

  const handleCampaignSelectionsChange = useCallback(
    (id: string, campaignSelections: Record<string, boolean>) => {
      updateModel(id, { campaignSelections });
    },
    [updateModel],
  );

  const handleAddModel = useCallback(() => {
    const newId = (models.length + 1).toString();
    const newModel = createEmptyModel(newId);
    // Pre-select all media and campaigns
    newModel.selectedMedia = [...mediaPlatforms];
    const campaignSelections: Record<string, boolean> = {};
    campaignsWithGroups.forEach((c) => {
      campaignSelections[c.campaignId] = true;
    });
    newModel.campaignSelections = campaignSelections;
    setModels((prev) => [...prev, newModel]);
  }, [models.length, mediaPlatforms, campaignsWithGroups]);

  const handleRemoveModel = useCallback((id: string) => {
    setModels((prev) => prev.filter((m) => m.id !== id));
  }, []);

  // Validation
  const isModelValid = useCallback((model: ModelFormData) => {
    const hasName = model.modelName.trim() !== '';
    const hasSource = model.kpiSource !== '';
    const hasMetric = model.kpiMetric !== '';
    const hasMedia = model.selectedMedia.length > 0;
    const hasSelectedCampaigns = Object.values(model.campaignSelections).some(
      (v) => v === true,
    );
    return (
      hasName && hasSource && hasMetric && hasMedia && hasSelectedCampaigns
    );
  }, []);

  const areAllModelsValid = useMemo(
    () => models.every(isModelValid),
    [models, isModelValid],
  );

  // Submit handlers
  const handleSubmit = useCallback(() => {
    if (!areAllModelsValid) return;

    const modelsPayload: CreateModelInput[] = models.map((m) => ({
      modelName: m.modelName.trim(),
      kpiSource: m.kpiSource,
      kpiMetric: m.kpiMetric,
      kpiCategory:
        !m.kpiCategory || m.kpiCategory === TOTAL_CATEGORY_VALUE
          ? null
          : m.kpiCategory.trim() || null,
      selectedMedia: m.selectedMedia,
      campaignSelections: m.campaignSelections,
    }));

    if (mode === 'create') {
      createMutation.mutate(
        { models: modelsPayload },
        {
          onSuccess: () => {
            showSuccess(
              'Models created successfully',
              'Your models are now being processed.',
            );
            onComplete?.();
          },
          onError: (error) => {
            const defaultMessage =
              'Please try again or contact support if the issue persists';
            const apiMessage = (
              error as { response?: { data?: { message?: string } } }
            )?.response?.data?.message;
            const errorMessage =
              !apiMessage || apiMessage === 'Internal server error.'
                ? defaultMessage
                : apiMessage;
            showError('Failed to create models', errorMessage);
          },
        },
      );
    } else if (mode === 'edit' && modelId) {
      updateMutation.mutate(
        {
          modelId,
          ...modelsPayload[0],
        },
        {
          onSuccess: () => {
            showSuccess(
              'Model updated successfully',
              'Your model has been updated.',
            );
            onComplete?.();
          },
          onError: (error) => {
            const defaultMessage =
              'Please try again or contact support if the issue persists';
            const apiMessage = (
              error as { response?: { data?: { message?: string } } }
            )?.response?.data?.message;
            const errorMessage =
              !apiMessage || apiMessage === 'Internal server error.'
                ? defaultMessage
                : apiMessage;
            showError('Failed to update model', errorMessage);
          },
        },
      );
    }
  }, [
    areAllModelsValid,
    models,
    mode,
    modelId,
    createMutation,
    updateMutation,
    onComplete,
    showSuccess,
    showError,
  ]);

  const handleCancel = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  const isLoading =
    isLoadingKpiSources ||
    isLoadingCampaigns ||
    isLoadingGroups ||
    isLoadingModel ||
    isLoadingKpi;

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  return {
    // State
    models,
    kpiSources,
    kpiMetrics,
    kpiCategoryOptions,
    mediaPlatforms,
    campaignsWithGroups,
    isLoading,
    isSubmitting,
    areAllModelsValid,

    // Toast state
    toastState,
    hideToast,

    // Handlers
    handleModelNameChange,
    handleSourceChange,
    handleSourceReset,
    handleMetricChange,
    handleMetricReset,
    handleCategoryChange,
    handleCategoryReset,
    handleMediaChange,
    handleCampaignSelectionsChange,
    handleAddModel,
    handleRemoveModel,
    handleSubmit,
    handleCancel,
  };
};
